import { Table as ChakraTable, Link, Icon, Text, chakra } from '@chakra-ui/react'
import { type LLMOutputComponent } from '@llm-ui/react'
import type { PropsWithChildren, ReactElement, Ref } from 'react'
import { memo } from 'react'
import { FiExternalLink } from 'react-icons/fi'
import type { Options } from 'react-markdown'
import Markdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { gruvboxLight as light, gruvboxDark as dark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import remarkImages from 'remark-images'
import remarkMath from 'remark-math'

import { useColorModeValue } from '@app/components/ui/color-mode'
import CopyButton from '@app/pages/strategies/components/aiChat/components/copyButton'

import 'katex/dist/katex.css'

const Code: Options['components']['code'] = ({ children, className, node: _, ref, ...rest }) => {
  const style = useColorModeValue(light, dark)
  const match = /language-(\w+)/.exec(className || '')

  return match ? (
    <SyntaxHighlighter
      {...rest}
      PreTag="div"
      language={match[1]}
      style={style}
      wrapLongLines
      codeTagProps={{ style: { whiteSpace: 'pre-wrap', overflowWrap: 'break-word' } }}
      ref={ref as unknown as Ref<SyntaxHighlighter>}
    >
      {String(children).replace(/\n$/, '')}
    </SyntaxHighlighter>
  ) : (
    <code {...rest} className={className}>
      {children}
    </code>
  )
}

const Pre: Options['components']['pre'] = ({ children, ...rest }) => {
  const content = (children as ReactElement<PropsWithChildren>)?.props?.children?.toString()

  return (
    <pre style={{ position: 'relative', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }} {...rest}>
      {content && <CopyButton position="absolute" right={2} top={2} content={content} />}
      {children}
    </pre>
  )
}

const ExternalLink: Options['components']['a'] = ({ children, ...rest }) => (
  <Link fontWeight="bolder" rel="onoopener noreferrer" target="_blank" {...rest}>
    {children}{' '}
    <Icon mx="2px">
      <FiExternalLink />
    </Icon>
  </Link>
)

const Paragraph: Options['components']['p'] = ({ children, ...rest }) => (
  <Text as="p" whiteSpace="pre-wrap" wordBreak="break-word" {...rest} my={1}>
    {children}
  </Text>
)

const Table: Options['components']['table'] = ({ children, ...rest }) => (
  <ChakraTable.Root whiteSpace="pre-wrap" wordBreak="break-word" {...rest} my={4}>
    {children}
  </ChakraTable.Root>
)

const Td: Options['components']['td'] = ({ children, ...rest }) => (
  <ChakraTable.Cell whiteSpace="pre-wrap" wordBreak="normal" {...rest}>
    {children}
  </ChakraTable.Cell>
)

const OrderedList = chakra('ol')
const ChakraOl: Options['components']['ol'] = ({ children, ...rest }) => (
  <OrderedList listStyleType="decimal" marginStart="1em" {...rest}>
    {children}
  </OrderedList>
)

const UnorderedList = chakra('ul')

const ChakraaUl: Options['components']['ul'] = ({ children, ...rest }) => (
  <UnorderedList listStyleType="initial" marginStart="1em" {...rest}>
    {children}
  </UnorderedList>
)

const ListItem = chakra('li')
const ChakraLi: Options['components']['li'] = ({ children, ...rest }) => <ListItem {...rest}>{children}</ListItem>

const COMPONENTS: Options['components'] = {
  a: ExternalLink,
  code: Code,
  p: Paragraph,
  pre: Pre,

  table: Table,
  thead: ChakraTable.Header as Options['components']['thead'],
  tbody: ChakraTable.Body as Options['components']['tbody'],
  th: ChakraTable.ColumnHeader as Options['components']['th'],
  tr: ChakraTable.Row as Options['components']['tr'],
  td: Td,

  ol: ChakraOl,
  ul: ChakraaUl,
  li: ChakraLi
}

const MessageContent: LLMOutputComponent = ({ blockMatch }) => {
  // Replace \[ with $$ and \] with $$ to ensure LaTeX compatibility
  const processedText = blockMatch?.output
    // multiline LaTeX looks like '[\n<indent spacing><formula>\n]', with spaces inside of the dollar signs
    ?.replace(/\\\\?\[([\s\\]+?[^\d\s][\s\S]*?)]/g, '$$$1$$')
    // inline LaTeX looks like ' ( <formula> ) ', with spaces inside of the parentheses
    ?.replace(/(?<=\s)\\?\(([\s\\]+?[^\d\s][\s\S]*?(?=\s))\)/g, '$$$1$$')

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkImages, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={COMPONENTS}
    >
      {processedText}
    </Markdown>
  )
}

export default memo(MessageContent)
